/**
 * DealHeader — Compact sub-header for deal flow pages.
 *
 * Single-row layout:
 *   Left:  back arrow + business name (truncated)
 *   Right: segmented progress bar + current step label
 *
 * Pattern: segmented bar (visual progress) + only the current step name.
 * Benchmarked against Shopify checkout, Stripe onboarding, Gusto setup flows.
 */
import React from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { DEAL_STAGES, getStepIndex } from '@/components/deal/mockDealData';

export default function DealHeader({ businessName, currentStage, onBack, rightSlot, stages = DEAL_STAGES }) {
  const stepIndex = getStepIndex(currentStage, stages);

  return (
    <div className="sticky top-16 z-40 bg-card/95 backdrop-blur-sm border-b border-border shadow-[0_1px_3px_0_rgba(0,0,0,0.04)]">
      <div className="mx-auto flex max-w-5xl min-w-0 items-center gap-2.5 px-4 py-2.5 sm:gap-3 sm:px-6">
        {/* Left: back + business name */}
        <div className="flex min-w-0 items-center gap-2">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="h-8 w-8 shrink-0"
              aria-label="Go back"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          {businessName && (
            <span className="text-sm font-medium text-foreground truncate max-w-[180px] sm:max-w-[240px]">
              {businessName}
            </span>
          )}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right: compact progress indicator */}
        <div className="flex shrink-0 items-center gap-2 sm:gap-2.5">
          {/* Segmented bar — each segment has title attr so hover reveals the
              upcoming step name. Helps non-expert sellers see the journey
              without bloating the sticky header. */}
          <div className="flex items-center gap-1" aria-label={`Step ${stepIndex + 1} of ${stages.length}: ${stages[stepIndex]?.title}`}>
            {stages.map((stage, i) => (
              <div
                key={stage.key}
                title={`Step ${i + 1} of ${stages.length}: ${stage.title}`}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-300',
                  i === stepIndex ? 'w-8 sm:w-10 bg-coral' : 'w-5 sm:w-6',
                  i < stepIndex && 'bg-coral',
                  i > stepIndex && 'bg-border',
                )}
              />
            ))}
          </div>

          {/* Current step label with explicit count */}
          <span className="max-w-[5.5rem] truncate text-xs font-medium text-foreground sm:max-w-none sm:text-sm">
            <span className="hidden sm:inline text-muted-foreground">Step {stepIndex + 1}/{stages.length} · </span>
            {stages[stepIndex]?.title}
          </span>
        </div>

        {/* Optional right slot */}
        {rightSlot && <div className="shrink-0">{rightSlot}</div>}
      </div>
    </div>
  );
}
